import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AdminPageHeader from '../common/AdminPageHeader';
import NoAccessGate from '../common/NoAccessGate';
import { NO_ACCESS_VARIANT } from '../common/NoAccessPanel';
import AdmitPatientForm from './AdmitPatientForm';
import { ROUTES } from '../../constants/routes';
import { ADMISSION_PERMISSIONS } from '../../constants/admission';

/**
 * AdmitPatientPage — route wrapper for AdmitPatientForm inside the shared branded
 * shell (AdminPageHeader). Return-nav (C6a / B4): when Manage Admission launches
 * the admit flow it passes `state.returnTo = pathname+search`, so BACK / CANCEL
 * restore the desk WITH its filters intact. A deep-link / refresh carries no state
 * → fall back to the org dashboard (mirrors Register Visit's cancel nav). A
 * SUCCESSFUL admit lands back on Manage Admission (the returnTo when it is a desk
 * URL, else the plain desk) with `highlightAdmissionId` + `admissionStatus` so the
 * desk can switch the chip, scroll + flash the new row.
 *
 * admission-access S1 — the whole page sits behind the Admit Patient gate: a
 * member without ADMIT_PATIENT sees the in-page no-access panel (the form never
 * mounts), not a form whose submit the server would refuse.
 */
export default function AdmitPatientPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo ?? ROUTES.ORG;
  // IP-3: one-shot ABHA hand-off from the ABHA page. Held in a ref (stable identity), and the
  // history entry is cleared so a refresh / back does not re-trigger it.
  const abhaHandoff = useRef(location.state?.abhaSnapshot || null);
  useEffect(() => {
    if (abhaHandoff.current) window.history.replaceState({}, '', window.location.pathname);
  }, []);
  // R146: the waiting list lives only on the OPD dashboard; a picked scan arrives here as this hand-off.
  const handoff = abhaHandoff.current;

  // Success target: preserve the desk's filters when we came from a desk URL,
  // otherwise land on the plain desk so the new row is visible (AC5).
  const successTarget = returnTo.startsWith(ROUTES.ADMISSIONS) ? returnTo : ROUTES.ADMISSIONS;
  // onAdmitted passes the created admission object; onViewAdmission (duplicate
  // link) passes a bare admission_id string — normalise both.
  const onDone = (result) => {
    const admission = typeof result === 'string' ? { admission_id: result } : (result || {});
    navigate(successTarget, {
      state: {
        highlightAdmissionId: admission.admission_id || null,
        admissionStatus: admission.status || null,
      },
    });
  };

  return (
    <NoAccessGate slug={ADMISSION_PERMISSIONS.ADMIT_PATIENT} deniedVariant={NO_ACCESS_VARIANT.ADMIT}>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <AdminPageHeader backTo={returnTo} backLabel="← Back" />
        <main className="mx-auto max-w-5xl px-4 py-6">
          <h1 className="mb-4 text-xl font-semibold text-slate-900 dark:text-slate-50">Register Admission</h1>
          <AdmitPatientForm
            key={handoff?.scanId || 'form'}
            onAdmitted={onDone}
            onViewAdmission={onDone}
            initialAbhaSnapshot={handoff}
          />
        </main>
      </div>
    </NoAccessGate>
  );
}
